import { useCallback, useEffect, useRef } from "react";
import Axios from "axios";
import { useAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import { useLogin } from "../login";
import {
    ConnectStatus,
    PayoutBalance,
    PayoutLedgerPage,
    PayoutLedgerQuery,
    payoutBalanceAtom,
    payoutLedgerAtom,
    payoutLoadingAtom,
    payoutStatusAtom,
} from "./types";

interface StartOnboardingResult {
    url: string;
    accountId: string;
}

export const usePayout = () => {
    const [status, setStatus] = useAtom(payoutStatusAtom);
    const [balance, setBalance] = useAtom(payoutBalanceAtom);
    const [ledger, setLedger] = useAtom(payoutLedgerAtom);
    const [loading, setLoading] = useAtom(payoutLoadingAtom);

    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);

    useEffect(() => {
        loginFunctionsRef.current = loginFunctions;
    }, [loginFunctions]);

    // GET /payout/status — null when the brand has no Connect account yet.
    const getStatus = useCallback(async (brand_id: number): Promise<ConnectStatus | null> => {
        const result = await Axios.get(`${environment.payoutUrl}/status?brand_id=${brand_id}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            setStatus(result.data as ConnectStatus);
            return result.data as ConnectStatus;
        }
        // No account / not-onboarded surfaces as an ErrorResponse — treat as "needs setup".
        setStatus(null);
        return null;
    }, [authToken]);

    // POST /payout/onboarding — mints a fresh single-use Stripe account link.
    const startOnboarding = useCallback(async (brand_id: number): Promise<StartOnboardingResult | null> => {
        const result = await Axios.post(`${environment.payoutUrl}/onboarding`, { brand_id }, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        return result ? (result.data as StartOnboardingResult) : null;
    }, [authToken]);

    // GET /payout/balance — owed/paid totals in integer cents.
    const getBalance = useCallback(async (brand_id: number): Promise<PayoutBalance | null> => {
        const result = await Axios.get(`${environment.payoutUrl}/balance?brand_id=${brand_id}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            setBalance(result.data as PayoutBalance);
            return result.data as PayoutBalance;
        }
        setBalance(null);
        return null;
    }, [authToken]);

    // GET /payout/ledger — server-side paginated history with optional date range.
    const getLedger = useCallback(async (brand_id: number, query: PayoutLedgerQuery): Promise<PayoutLedgerPage | null> => {
        setLoading(true);
        const params = new URLSearchParams();
        params.set('brand_id', String(brand_id));
        params.set('limit', String(query.limit));
        params.set('offset', String(query.offset));
        if (query.from) params.set('from', query.from);
        if (query.to) params.set('to', query.to);

        const result = await Axios.get(`${environment.payoutUrl}/ledger?${params.toString()}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        setLoading(false);
        if (result) {
            setLedger(result.data as PayoutLedgerPage);
            return result.data as PayoutLedgerPage;
        }
        return null;
    }, [authToken]);

    return { status, balance, ledger, loading, getStatus, startOnboarding, getBalance, getLedger };
};
